import { AlertTriangle, ChevronLeft, ChevronRight, Database, FileText, HelpCircle, Info, Key, Link, Loader2, Network, Radar, Shield, Sparkles, User, Video } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import StatusIndicator from "../StatusIndicator";
import { Button } from "../../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import OverviewDetails from "../../Overview/OverviewDetails";
import AlertDetails from "../../Alerts/AlertsDetails";
import LolbinDetails from "../../Lolbin/LolbinDetails";
import { Computer, Device } from "../../interfaces/EndpointInterfaces";
import { connectComputer, getComputersPerDevice } from "../Api/EndpointApi";
import useUserStore from "../../../../services/userStore";
import { myLocalStorage } from "../../../StorageHelper";
import AuditLogsDetails from "../../AuditLogs/AuditLogsDetails";
import RecordingDetails from "../../Recordings/RecordingDetails";
import CrendentialDetails from "../../Credentials/CredentialDetails";
import GatewayCredentialDetails from "../../Credentials/GatewayCredentialDetails";
import { CredentialInterface } from "../../Credentials/interfaces/credentialinterface";
import { getCredentialsList } from "../../Credentials/Api/credentialapi";
import AdminWindowsConnectDialog from "../AdminWindowsConnectDialog";
import ResourceRequestsDeviceDetails from "./ResourceRequestsDeviceDetails";
import ResourceDetails from "../../Resources/ResourceDetails";
import SSHUsersTab from "../../SSHUsers/SSHUsersTab";
import { getOSIcon, transformApiToDevicePerDevice } from "../helper";
import SubnetsTab from "../../Subnets/SubnetsTab";
import GatewayDevicesTab from "../../GatewayDevices/GatewayDevicesTab";
import DiscoveredDevicesTab from "../DiscoveredDevices/DiscoveredDevicesTab";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import { Badge } from "../../../ui/badge";
import AgenticActivity from "../../AiAgentActivity/AgenticActivity";


export default function DeviceDetailPage() {
  const baseTabs = [
    { id: "overview", label: "Overview", icon: Info },
    { id: "alerts", label: "Alerts", icon: AlertTriangle },
    { id: "lobsm", label: "LOLBin", icon: Database },
    { id: "resources", label: "Resources", icon: Shield },
    { id: "audit-logs", label: "Audit Logs", icon: FileText },
    { id: "recordings", label: "Recordings", icon: Video },
    { id: "resource-requests", label: "Resource Requests", icon: HelpCircle },
  ];
  const manualTabs = [
    { id: "recordings", label: "Recordings", icon: Video },
    { id: "credentials", label: "Credentials", icon: Key },
    { id: "resource-requests", label: "Resource Requests", icon: HelpCircle },
  ];
  const [activeTab, setActiveTab] = useState("overview");
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [device, setDevice] = useState<Device>();
  const { adminAccess } = location.state || {};
  const { role, email, userId } = useUserStore((state) => state.user);
  const setSessionTab = useUserStore((state) => state.setSessionTab);
  let tenant = myLocalStorage.getItem("latestTenant");
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const [showAdminConnectDialog, setShowAdminConnectDialog] = useState(false);
  const [credentials, setCredentials] = useState<CredentialInterface[] | null>(null);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const deviceToComputer = (device: Device): Computer => ({
    id: device?.id,
    guachost: device?.guachost ?? "",
    OperatingSystem: device?.os ?? device?.OperatingSystem ?? "",
    accessRemainingTime: device?.accessRemainingTime,
    whiteswanAccessGroupId: device?.whiteswanAccessGroupId,
    profileMode: device?.profileMode,
    mode: device?.mode,
  });

  const getCredentials = async (deviceId: string, isManual?: boolean) => {
    const response = await getCredentialsList(tenantName, deviceId);
    const mergedCredentials: CredentialInterface[] = isManual
      ? response
      : [{ credentialUsername: "wsadmin", credentialLabel: "wsadmin", createdDate: new Date().toISOString(), id: 0 }, ...response];
    setCredentials(mergedCredentials);
    setShowAdminConnectDialog(true);
  }

  const isDeviceWindows = (device: Device) => {
    if (device?.os?.trim().length > 0) {
      return device?.os.toLowerCase().includes("windows");
    } else {
      return true;
    }
  };

  const handleConnect = (device: Device) => {

    if (isDeviceWindows(device) || device?.isManuallyAddedMachine) {
      getCredentials(device?.id, device?.isManuallyAddedMachine);
      console.log('Windows/manual device connect clicked:', device);

    } else {
      setShowAdminConnectDialog(false);
      handleDetailsPageConnect(device, "", "");
    }
  };


  const handleDetailsPageConnect = async (device: Device, mode: string, label: string) => {
    const computer: Computer = deviceToComputer(device);
    setIsConnecting(true);

    try {
      const userType = role === "TENANT_USER" ? "user" : "admin";
      await connectComputer(computer, email, userType, mode, label, userId, setSessionTab);
    } catch (error) {
      console.error("Failed to connect:", error);
    }
    setIsConnecting(false);
  };

  const connectAccessDisable = (device: Device) => {
    switch (adminAccess) {
      case "NO_ACCESS":
        return true;

      case "SERVER_ONLY":
        const os = device.OperatingSystem?.toLowerCase() ?? "";
        if (device?.isManuallyAddedMachine) {
          return false;
        } else if (
          os.includes("linux") ||
          (os.includes("windows") && os.includes("server"))
        ) {
          return false;
        } else {
          return true;
        }

      default:
        return false;
    }
  };


  const connectButtonEnabled = (device: Device) => {
    const isEnabled =
      device.connectionCreatedFor?.some(
        (user) => userId === user.userId && role === user.role
      ) ?? false;

    return isEnabled;
  };

  const fetchDeviceList = useCallback(async (isFirst = false) => {
    setIsPageLoading(true);
    try {
      let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
      const response = await getComputersPerDevice({
        tenantName: tenantName!,
        email: email!,
        role: role!,
        userId: userId!,
        id: id!
      });


      let deviceResponse: Device = transformApiToDevicePerDevice(response);

      if (deviceResponse)
        setDevice(deviceResponse);
      else
        setDevice(undefined);





    } catch (error) {
      console.error("Failed to fetch devices", error);
      setDevice(undefined);
    } finally {
      setIsPageLoading(false);
    }
  }, [email, role, userId, id]);



  useEffect(() => {
    fetchDeviceList()
  }, [tenantName, email, role, userId, id]);

  const tabs = useMemo(() => {
    if (!device) {
      return baseTabs;
    }

    if (device.isManuallyAddedMachine) {
      return manualTabs;
    }

    if (isDeviceWindows(device)) {
      return [
        ...baseTabs,
        { id: "credentials", label: "Credentials", icon: Key },
        { id: "aiagenticactivity", label: "Agentic Activity", icon: Sparkles },
      ];
    }

    if (device.isGateway) {
      return [
        ...baseTabs,
        { id: "credentials", label: "Credentials", icon: Key },
        { id: "subnets", label: "Subnets", icon: Key },
        { id: "gateway-devices", label: "Gateway devices", icon: Network },
        { id: "discovered-devices", label: "Discovered Devices", icon: Radar },
        { id: "ssh-users", label: "SSH Users", icon: User },
      ];
    }

    return [
      ...baseTabs,
      { id: "ssh-users", label: "SSH Users", icon: User },
    ];
  }, [device]);

  useEffect(() => {
    if (!device) {
      return;
    }

    const fallbackTab = device.isManuallyAddedMachine ? "recordings" : tabs[0]?.id || "overview";
    const hasActiveTab = tabs.some((tab) => tab.id === activeTab);

    if (!hasActiveTab) {
      setActiveTab(fallbackTab);
    }
  }, [device, tabs, activeTab]);

  const labelStatus = (mode: string, osmode: string) => {
    let status = mode
    if (mode === "INHERITED_FROM_PROFILE") status = osmode;
    if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
    return status.toUpperCase();

  }

  const getOperatingModeColor = (mode: string, osMode: string) => {
    let status = labelStatus(mode, osMode);
    switch (status.toLowerCase()) {
      case "enforcing":
        return "destructive";
      case "auditing":
        return "default";
      case "disabled":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background">

      {/* Breadcrumb Navigation */}
      <div className="px-6 py-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">

            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="p-0 h-auto hover:bg-transparent"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>


            <span>Tenants</span>
            <ChevronRight className="w-4 h-4" />
            <span>{tenantName}</span>
            <ChevronRight className="w-4 h-4" />
            <span>Computers</span>
            <ChevronRight className="w-4 h-4" />
            <span className="font-medium text-foreground">{device?.name || "N/A"}</span>
          </div>
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-8 h-8 ">
                {getOSIcon(device?.os || "")}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-3">
                  <h1 className="text-xl font-bold text-foreground" data-testid="text-device-name">
                    {device?.name}
                  </h1>
                  <StatusIndicator lastSeen={device?.lastSeen || "offline"} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                </div>
                <span className="mt-1 text-sm text-muted-foreground">{device?.os}</span>
                <div className="flex items-center gap-2 mt-1 text-sm">

                  <Badge
                    variant={getOperatingModeColor(device?.mode || "", device?.profileMode || "")}
                    className="text-xs capitalize"
                  >
                    {labelStatus(device?.mode || "", device?.profileMode || "")}
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              data-testid="button-connect"

              disabled={
                isConnecting ||
                !device?.isNebulaEnabled ||
                (connectAccessDisable(device) &&
                  role !== "TENANT_USER") ||
                (!isDeviceWindows(device) &&
                  !connectButtonEnabled(device) &&
                  !device?.isManuallyAddedMachine)
              }
              onClick={() => {
                if (device)
                  handleConnect(device);
              }}
            >
              <Link className="w-4 h-4 mr-2" />
              Connect
              {isConnecting && <Loader2 className="w-4 h-4 text-white-600 dark:text-white-400 animate-spin" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="relative border-b bg-slate-50 dark:bg-slate-900">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-slate-50 via-slate-50/85 to-transparent dark:from-slate-900 dark:via-slate-900/85" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-slate-50 via-slate-50/85 to-transparent dark:from-slate-900 dark:via-slate-900/85" />
          <div className="custom-scrollbar overflow-x-auto px-12 scroll-smooth [scroll-padding-inline:3rem]">
            <TabsList className="inline-flex h-12 min-w-max gap-8 border-none bg-transparent p-0">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none bg-transparent px-0 pb-3 pt-3 font-medium text-muted-foreground data-[state=active]:text-foreground hover:text-foreground"
                  data-testid={`tab-${tab.id}`}
                >
                  <div className="flex items-center gap-2">
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Show skeleton when loading */}
          {isPageLoading ? (
            <div className="flex flex-col gap-4 overflow-y-auto p-6">
              <Skeleton className="w-40 h-6" />
              <Skeleton className="w-12 h-4 ml-auto" />
              <Skeleton className="w-full h-64" />
              <Skeleton className="w-1/2 h-6 mt-4" />
            </div>
          ) : !device ? (
            // Show empty-state message if device is null
            <div className="flex h-full items-center justify-center overflow-y-auto text-sm font-medium text-gray-500">
              Unable to fetch details of devices.
            </div>
          ) : (
            // 🔹 Render tabs only if device exists and not loading
            <>
              <TabsContent value="overview" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <OverviewDetails onSelectorChange={() => { }} fromdetails={true} computerName={device?.name} computerId={device?.id} />
              </TabsContent>

              <TabsContent value="alerts" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <AlertDetails fromdetails={true} computerName={device?.name} computerId={device?.id} />
              </TabsContent>

              <TabsContent value="lobsm" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <LolbinDetails
                  fromdetails={true}
                  computerName={device?.name}
                  computerId={device?.id}
                  showTopLolBins={true}
                  showTopUsers={true}
                  showTopComputers={false}
                />
              </TabsContent>

              <TabsContent value="resources" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <ResourceDetails device={device} />
              </TabsContent>

              <TabsContent value="audit-logs" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <AuditLogsDetails fromdetails={true} computerName={device?.name} computerId={device?.id} />
              </TabsContent>

              <TabsContent value="recordings" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <RecordingDetails
                  computerName={device?.name}
                  deviceId={device?.id}
                  guachost={device?.guachost || ""}
                />
              </TabsContent>

              <TabsContent value="resource-requests" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <ResourceRequestsDeviceDetails computerName={device?.name} />
              </TabsContent>

              <TabsContent value="credentials" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                {device.isGateway ? (
                  <GatewayCredentialDetails device={device} />
                ) : (
                  <CrendentialDetails device={device} />
                )}
              </TabsContent>

              <TabsContent value="aiagenticactivity" className="mt-0 min-h-0 flex-1 overflow-y-auto">

                <AgenticActivity device={device} global={false} />

              </TabsContent>

              <TabsContent value="ssh-users" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                <SSHUsersTab device={device} />
              </TabsContent>

              {device.isGateway && (
                <>
                  <TabsContent value="gateway-devices" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                    <GatewayDevicesTab device={device} />
                  </TabsContent>

                  <TabsContent value="discovered-devices" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                    <DiscoveredDevicesTab device={device} />
                  </TabsContent>

                  <TabsContent value="subnets" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                    <SubnetsTab device={device} />
                  </TabsContent>
                </>
              )}
            </>
          )}
        </div>
      </Tabs>


      <AdminWindowsConnectDialog
        open={showAdminConnectDialog}
        onOpenChange={setShowAdminConnectDialog}
        deviceName={device?.name}
        deviceId={device?.id || ""}
        credentials={credentials || []}
        onConnect={(deviceId, username, mode, label) => {
          if (device) {
            handleDetailsPageConnect(device, mode, label);
          }
          setShowAdminConnectDialog(false);
        }
        }
      />

      {device && (
        <></>
      )}

    </div>
  );
}